import React from 'react';
import DataFallback from './DataFallback';
import { ERROR_KIND, TERMINAL_COPY } from '../../utils/errorTaxonomy';

/**
 * Keeps a render-time throw inside one region of the page.
 *
 * Until this existed the app had no error boundary at all, so any exception
 * during render unmounted the entire SPA and left a white page — the user lost
 * the nav, the header, and every way out except reloading.
 *
 * Deliberately router-free so it can wrap any subtree and be tested on its own;
 * `TerminalErrorBoundary` is the thin wrapper that feeds it the current path.
 *
 * @param {string} [resetKey]  Changing this clears the error. Pass the route so
 *                             navigating away is a working escape hatch.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, remountKey: 0, lastResetKey: props.resetKey };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  static getDerivedStateFromProps(props, state) {
    if (props.resetKey !== state.lastResetKey) {
      return { hasError: false, lastResetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error, errorInfo) {
    // The stack is for us, never for the customer.
    console.error(
      `[${this.props.label || 'ErrorBoundary'}] render failed:`,
      error,
      errorInfo && errorInfo.componentStack
    );
  }

  handleRetry = () => {
    // Remount the subtree. A genuinely transient throw recovers here, with no
    // page reload and no lost session.
    this.setState((s) => ({ hasError: false, remountKey: s.remountKey + 1 }));
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <DataFallback
          variant={this.props.variant || 'pane'}
          error={{
            kind: ERROR_KIND.UNKNOWN,
            title: TERMINAL_COPY.unknown.title,
            message: TERMINAL_COPY.unknown.message,
            retryable: true,
          }}
          onRetry={this.handleRetry}
          actionLabel="Try again"
          // A render crash is not an outage: the server may be perfectly
          // healthy, so polling it would never clear this and the countdown
          // would promise a recovery that cannot arrive. Remounting is the only
          // thing that helps, and only the user can ask for it.
          autoRecover={false}
        >
          <button type="button" onClick={this.handleReload} className="boundaryReloadBtn">
            {this.props.reloadLabel || 'Reload page'}
          </button>
        </DataFallback>
      );
    }

    return <React.Fragment key={this.state.remountKey}>{this.props.children}</React.Fragment>;
  }
}

export default ErrorBoundary;
